/**
 * IoT Shack Playground
 * Inspect Timescale telemetry + templates for a device identifier.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchPlayground } from './api';
import type { PlaygroundResponse } from './types';

const WINDOW_OPTIONS = [
  { label: 'Last 15 minutes', value: 15 },
  { label: 'Last 1 hour', value: 60 },
  { label: 'Last 4 hours', value: 240 },
  { label: 'Last 24 hours', value: 1440 },
  { label: 'Custom', value: -1 },
];

export default function Playground() {
  const [identifier, setIdentifier] = useState('');
  const [windowPreset, setWindowPreset] = useState<number>(60);
  const [customMinutes, setCustomMinutes] = useState('60');
  const [limit, setLimit] = useState('200');
  const [submitted, setSubmitted] = useState<{
    identifier: string;
    windowMinutes: number;
    limit: number;
  } | null>(null);
  const [showRaw, setShowRaw] = useState(true);
  const [showEnriched, setShowEnriched] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshMs, setRefreshMs] = useState(5000);

  const queryParams = useMemo(() => {
    if (!submitted) return null;
    return {
      identifier: submitted.identifier,
      windowMinutes: submitted.windowMinutes,
      limit: submitted.limit,
    };
  }, [submitted]);

  const { data, isLoading, error, dataUpdatedAt, refetch } = useQuery<PlaygroundResponse>({
    queryKey: ['iot', 'playground', queryParams],
    queryFn: () => fetchPlayground(queryParams!),
    enabled: !!queryParams,
    refetchInterval: autoRefresh && queryParams ? refreshMs : false,
    refetchIntervalInBackground: true,
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const id = identifier.trim();
    if (!id) return;
    const minutes = windowPreset === -1 ? Number(customMinutes || 60) : windowPreset;
    const safeMinutes = Number.isFinite(minutes) && minutes > 0 ? minutes : 60;
    const safeLimit = Math.max(1, Math.min(2000, parseInt(limit || '200', 10) || 200));
    setSubmitted({ identifier: id, windowMinutes: safeMinutes, limit: safeLimit });
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">IoT Shack Playground</h1>
        <p className="text-gray-600 mt-1">
          Inspect raw + enriched telemetry, linked templates, and device context for a sensor.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">Device identifier (IMEI or MAC)</label>
            <input
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="e.g. 123456789012345 or AA:BB:CC:DD:EE:FF"
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Window</label>
            <select
              value={windowPreset}
              onChange={(e) => setWindowPreset(Number(e.target.value))}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              {WINDOW_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Limit</label>
            <input
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
        </div>

        {windowPreset === -1 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Custom minutes</label>
              <input
                value={customMinutes}
                onChange={(e) => setCustomMinutes(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="submit"
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Load telemetry
          </button>
          <button
            type="button"
            onClick={() => refetch()}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            disabled={!queryParams}
          >
            Refresh now
          </button>
          <label className="inline-flex items-center gap-2 text-sm text-gray-600">
            <input type="checkbox" checked={showRaw} onChange={() => setShowRaw(!showRaw)} />
            Show raw payload
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-gray-600">
            <input type="checkbox" checked={showEnriched} onChange={() => setShowEnriched(!showEnriched)} />
            Show enriched (IKU)
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-gray-600">
            <input type="checkbox" checked={autoRefresh} onChange={() => setAutoRefresh(!autoRefresh)} />
            Auto refresh
          </label>
          <div className="inline-flex items-center gap-2 text-sm text-gray-600">
            <span>Interval</span>
            <select
              value={refreshMs}
              onChange={(e) => setRefreshMs(Number(e.target.value))}
              className="rounded border border-gray-300 px-2 py-1 text-sm"
              disabled={!autoRefresh}
            >
              <option value={5000}>5s</option>
              <option value={10000}>10s</option>
              <option value={30000}>30s</option>
            </select>
          </div>
        </div>
      </form>

      {isLoading && (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Failed to load playground: {String(error)}</p>
        </div>
      )}

      {data && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <InfoCard title="Device">
              <KeyValue label="Resolved ID" value={data.resolved_device_id} />
              <KeyValue label="Status" value={data.device?.status || 'unknown'} />
              <KeyValue label="Type" value={data.device?.type || 'unknown'} />
              <KeyValue label="Template" value={data.device?.templateId || '—'} />
            </InfoCard>
            <InfoCard title="Templates">
              <KeyValue label="Device template" value={data.templates.deviceTemplateId || '—'} />
              <KeyValue label="Product template" value={data.templates.productTemplateId || '—'} />
              <KeyValue label="Telemetry template" value={data.templates.telemetryTemplateId || '—'} />
              <KeyValue label="Model template" value={data.templates.modelTemplateId || '—'} />
            </InfoCard>
            <InfoCard title="Telemetry">
              <KeyValue label="Timescale connected" value={data.timescale_connected ? 'yes' : 'no'} />
              <KeyValue label="Rows" value={String(data.telemetry.count)} />
              <KeyValue label="Window (minutes)" value={String(data.windowMinutes)} />
              <KeyValue
                label="Last update"
                value={dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleTimeString() : '—'}
              />
            </InfoCard>
          </div>

          {data.pim_product && (
            <InfoCard title="PIM Device Product">
              <KeyValue label="ID" value={data.pim_product.id || '—'} />
              <KeyValue label="Name" value={data.pim_product.name || '—'} />
              <KeyValue label="Template" value={data.pim_product.template_id || '—'} />
            </InfoCard>
          )}

          {data.model_product && (
            <InfoCard title="PIM Model Product">
              <KeyValue label="ID" value={data.model_product.id || '—'} />
              <KeyValue label="Name" value={data.model_product.name || '—'} />
              <KeyValue label="Template" value={data.model_product.template_id || '—'} />
            </InfoCard>
          )}

          <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Telemetry rows</h2>
              <span className="text-sm text-gray-500">{data.telemetry.count} records</span>
            </div>
            {data.telemetry.items.length === 0 ? (
              <p className="text-sm text-gray-500 mt-3">No telemetry rows found for this window.</p>
            ) : (
              <div className="mt-4 space-y-4">
                {data.telemetry.items.map((row, idx) => (
                  <div key={`${row.time}-${idx}`} className="border border-gray-100 rounded-lg p-3 bg-gray-50/40">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-sm text-gray-700">
                      <KeyValue label="Time" value={row.time} />
                      <KeyValue label="Device" value={row.device_id} />
                      <KeyValue label="Entity type" value={row.entity_type || '—'} />
                      <KeyValue label="Template" value={row.template_id || '—'} />
                    </div>
                    <div className="mt-3 grid grid-cols-1 xl:grid-cols-2 gap-3">
                      {showRaw && (
                        <div>
                          <p className="text-xs font-semibold text-gray-600 mb-1">Raw payload</p>
                          <pre className="text-xs bg-white border border-gray-200 rounded p-2 overflow-auto max-h-64">
                            {JSON.stringify(row.raw_payload, null, 2)}
                          </pre>
                        </div>
                      )}
                      {showEnriched && (
                        <div>
                          <p className="text-xs font-semibold text-gray-600 mb-1">Enriched (IKU)</p>
                          <pre className="text-xs bg-white border border-gray-200 rounded p-2 overflow-auto max-h-64">
                            {JSON.stringify(row.iku_value, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function InfoCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      <h2 className="text-sm font-semibold text-gray-800 mb-2">{title}</h2>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function KeyValue({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-xs text-gray-500">{label}</span>
      <span className="text-sm text-gray-900 break-all">{value}</span>
    </div>
  );
}
